import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BellRing, CheckCheck, CircleCheck, RefreshCw, Settings2, ShieldAlert, X } from 'lucide-react';
import { Button, useToast } from '@missions/ui';
import { Layer } from '../../components/Layer';
import { useApi } from '../../lib/hooks';
import { enablePush, pushPermission, type PushState } from '../../lib/push';
import { NotificationFeed, NotifTabs, type NotifTab, useMarkAllRead } from './NotificationList';

const DISMISS_KEY = 'ms-push-softask-dismissed';

/** Designed soft-ask shown BEFORE the browser's permission prompt (PLAN §8A). */
export function PushCard() {
  const toast = useToast();
  const [state, setState] = useState<PushState | 'just-on' | 'dismissed'>(() => {
    try {
      if (localStorage.getItem(DISMISS_KEY) === '1' && pushPermission() === 'default') return 'dismissed';
    } catch {
      /* private mode */
    }
    return pushPermission();
  });
  const [busy, setBusy] = useState(false);
  const enable = async () => {
    setBusy(true);
    try {
      const r = await enablePush();
      if (r === 'granted') {
        setState('just-on');
        toast('Push notifications enabled on this device');
      } else setState(r);
    } catch {
      toast('Couldn’t turn on push on this device. Try again later.', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };
  if (state === 'default')
    return (
      <div className="pushcard">
        <span className="pi2"><BellRing /></span>
        <div style={{ minWidth: 0 }}>
          <b>Get notified even when the app is closed</b>
          <p>Turn on push to hear about new assignments, mentions and due dates straight away on this device.</p>
          <div className="acts">
            <Button variant="primary" loading={busy} onClick={() => void enable()}>Enable push</Button>
            <Button
              variant="ghost"
              onClick={() => {
                try {
                  localStorage.setItem(DISMISS_KEY, '1');
                } catch {
                  /* private mode */
                }
                setState('dismissed');
                toast('No problem. You can turn on push any time in Preferences.', { type: 'info' });
              }}
            >
              Not now
            </Button>
          </div>
        </div>
      </div>
    );
  if (state === 'denied')
    return (
      <div className="pushcard warn">
        <span className="pi2"><ShieldAlert /></span>
        <div style={{ minWidth: 0 }}>
          <b>Push notifications are blocked</b>
          <p>Your browser is blocking notifications for this site. To turn them back on:</p>
          <ol>
            <li>Click the lock icon next to the address bar</li>
            <li>Open <b>Site settings → Notifications</b></li>
            <li>Choose <b>Allow</b>, then reload this page</li>
          </ol>
          <div className="acts">
            <Button onClick={() => setState(pushPermission())}><RefreshCw />I’ve allowed it</Button>
          </div>
        </div>
      </div>
    );
  if (state === 'just-on')
    return (
      <div className="pushcard ok">
        <span className="pi2"><CircleCheck /></span>
        <div>
          <b>Push is on for this device</b>
          <p style={{ marginBottom: 0 }}>You can choose which updates to get in Preferences.</p>
        </div>
      </div>
    );
  return null;
}

/** Bell panel: popover on desktop, full-screen sheet on phones (mockups/app.html #npanel). */
export function NotificationsPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [tab, setTab] = useState<NotifTab>('all');
  const counts = useApi('notifications.counts', undefined, { enabled: open });
  const markAll = useMarkAllRead();
  const nav = useNavigate();
  const unseen = counts.data?.unseen ?? 0;
  return (
    <Layer open={open} onRequestClose={onClose} className="npanel" clear label="Notifications" modal={false}>
      <div className="np-head">
        <button className="ib mob" onClick={onClose} aria-label="Close notifications"><ArrowLeft /></button>
        <h2>Notifications {unseen > 0 && <span className="new-c">{unseen} new</span>}</h2>
        <button className="btn btn-g sm" onClick={() => void markAll()}><CheckCheck />Mark all as read</button>
        <button
          className="ib"
          onClick={() => {
            onClose();
            nav('/settings/notifications');
          }}
          aria-label="Notification preferences"
        >
          <Settings2 />
        </button>
        <button className="ib hide-m" onClick={onClose} aria-label="Close"><X /></button>
      </div>
      <NotifTabs tab={tab} onTab={setTab} />
      <div className="nscroll">
        <PushCard />
        {open && <NotificationFeed tab={tab} onOpened={onClose} />}
      </div>
      <div className="np-foot">
        <div className="legend" aria-label="Legend">
          <span><i className="lg unseen" />New</span>
          <span><i className="lg seen" />Seen, unread</span>
          <span><i className="lg read" />Read</span>
        </div>
        <span className="sp" />
        <button
          className="btn btn-g sm"
          onClick={() => {
            onClose();
            nav('/inbox');
          }}
        >
          Open Inbox <ArrowRight />
        </button>
      </div>
    </Layer>
  );
}
